import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  clearAccessSession,
  connectInjectedWallet,
  getAccessStatus,
  getStoredAccessSession,
  loginAdmin as requestAdminLogin,
  logoutAccessSession,
  saveAccessSession,
  setupAdmin as requestAdminSetup,
  updateAdminCredentials as requestCredentialUpdate,
} from '../services/accessControlService';

const AccessContext = createContext(null);

export function useAccess() {
  const context = useContext(AccessContext);
  if (!context) throw new Error('useAccess must be used within AccessProvider');
  return context;
}

export function AccessProvider({ children }) {
  const [session, setSession] = useState(() => getStoredAccessSession());
  const [initialized, setInitialized] = useState(null);
  const [checking, setChecking] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState('');

  const applySession = useCallback((result) => {
    const nextSession = { token: result.token, identity: result.identity };
    saveAccessSession(nextSession);
    setSession(nextSession);
    setError('');
    return nextSession;
  }, []);

  const refresh = useCallback(async () => {
    setChecking(true);
    try {
      const status = await getAccessStatus();
      setInitialized(status.initialized);
      setError('');
      if (session && !status.identity) {
        clearAccessSession();
        setSession(null);
      } else if (session && status.identity) {
        setSession((current) => ({ ...current, identity: status.identity }));
      }
      return status;
    } catch {
      setError('權限服務目前無法連線');
      return null;
    } finally {
      setChecking(false);
    }
  }, [session]);

  useEffect(() => {
    refresh();
    // Initial session validation only. Later auth actions update state directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAuthentication = useCallback(async (operation) => {
    setAuthenticating(true);
    setError('');
    try {
      const result = await operation();
      return applySession(result);
    } catch (authError) {
      setError(authError.message || '身分驗證失敗');
      throw authError;
    } finally {
      setAuthenticating(false);
    }
  }, [applySession]);

  const connectWallet = useCallback(
    () => runAuthentication(connectInjectedWallet),
    [runAuthentication]
  );

  const loginAdmin = useCallback(
    (username, password) => runAuthentication(() => requestAdminLogin(username, password)),
    [runAuthentication]
  );

  const setupAdmin = useCallback(async (username, password) => {
    const nextSession = await runAuthentication(() => requestAdminSetup(username, password));
    setInitialized(true);
    return nextSession;
  }, [runAuthentication]);

  const updateCredentials = useCallback(
    (username, password) => runAuthentication(() => requestCredentialUpdate(username, password)),
    [runAuthentication]
  );

  const logout = useCallback(async () => {
    await logoutAccessSession();
    clearAccessSession();
    setSession(null);
    setError('');
  }, []);

  const value = {
    session,
    identity: session?.identity || null,
    initialized,
    checking,
    authenticating,
    error,
    clearError: () => setError(''),
    refresh,
    connectWallet,
    loginAdmin,
    setupAdmin,
    updateCredentials,
    logout,
  };

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}
